import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import OptimizedImage from '../../components/base/OptimizedImage';
import Breadcrumbs from '../../components/feature/Breadcrumbs';

const ContactoPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    projectType: '',
    area: '',
    neighborhood: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contacto - PLADUREX | Presupuesto de Pladur en Sabadell';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contacta con PLADUREX para tu proyecto de pladur en Sabadell. Presupuesto gratuito y sin compromiso. Llama al 629 501 941 o envía WhatsApp. Respuesta rápida.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'contacto pladur Sabadell, presupuesto pladur, teléfono pladurista Sabadell, WhatsApp pladur');
    }

    // Schema.org JSON-LD
    const contactSchema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "PLADUREX",
        "telephone": "+34629501941",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+34629501941",
          "contactType": "customer service",
          "areaServed": "ES",
          "availableLanguage": "Spanish"
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Sabadell"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Vallès Occidental"
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(contactSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d61mei8omoleh5thvmrg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          projectType: '',
          area: '',
          neighborhood: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: 'Teléfono',
      value: '+34 629 501 941',
      link: 'tel:+34629501941',
      description: 'Llámanos de lunes a viernes'
    },
    {
      icon: 'ri-whatsapp-line',
      title: 'WhatsApp',
      value: '629 501 941',
      link: 'https://wa.me/34629501941',
      description: 'Escríbenos en cualquier momento'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Zona de servicio',
      value: 'Sabadell, Vallès, Barcelona',
      link: null,
      description: 'Trabajamos en toda la comarca'
    },
    {
      icon: 'ri-time-line',
      title: 'Horario',
      value: 'Consultar',
      link: null,
      description: 'Adaptamos horarios a tu proyecto'
    }
  ];

  return (
    <div className="min-h-screen bg-dark-bg text-light-text">
      <Header />
      <Breadcrumbs />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="https://public.readdy.ai/ai/img_res/72e8f9d13a89b8dab1fc965b3a50c2e1.jpg"
            alt="Contacto PLADUREX"
            loading="lazy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-dark-bg"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              Contacta con <span className="text-primary-orange">nosotros</span>
            </h1>
            <p className="text-xl md:text-2xl text-light-muted max-w-3xl mx-auto">
              Presupuesto gratuito y sin compromiso para tu proyecto de pladur en Sabadell
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <h2 className="sr-only">Información de contacto</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-dark-card p-6 rounded-xl border border-dark-lighter text-center"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-primary-orange/10 rounded-full mx-auto mb-4">
                  <i className={`${info.icon} text-2xl text-primary-orange`}></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">{info.title}</h3>
                {info.link ? (
                  <a
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-primary-orange font-medium hover:text-primary-orange-light transition-colors whitespace-nowrap"
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-light-text font-medium">{info.value}</p>
                )}
                <p className="text-light-muted text-sm mt-2">{info.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-dark-card p-8 rounded-2xl border border-dark-lighter"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
                  Solicita tu <span className="text-primary-orange">presupuesto</span>
                </h2>
                <p className="text-light-muted">
                  Rellena el formulario y nos pondremos en contacto contigo lo antes posible. Conoce más sobre <Link to="/empresa" className="text-primary-orange hover:underline">nuestra empresa</Link> y <Link to="/servicios" className="text-primary-orange hover:underline">los servicios que ofrecemos</Link>.
                </p>
              </div>

              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Nombre completo <span className="text-primary-orange">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-dark-bg border border-dark-lighter rounded-lg text-light-text text-sm focus:outline-none focus:border-primary-orange transition-colors"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2">
                        Teléfono <span className="text-primary-orange">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-dark-bg border border-dark-lighter rounded-lg text-light-text text-sm focus:outline-none focus:border-primary-orange transition-colors"
                        placeholder="629 501 941"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="projectType" className="block text-sm font-medium mb-2">
                      Tipo de proyecto <span className="text-primary-orange">*</span>
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-lighter rounded-lg text-light-text text-sm focus:outline-none focus:border-primary-orange transition-colors cursor-pointer"
                    >
                      <option value="">Selecciona una opción</option>
                      <option value="Tabiques de pladur">Tabiques de pladur</option>
                      <option value="Trasdosados">Trasdosados</option>
                      <option value="Falsos techos">Falsos techos</option>
                      <option value="Aislamiento acústico">Aislamiento acústico</option>
                      <option value="Aislamiento térmico">Aislamiento térmico</option>
                      <option value="Pladur antihumedad">Pladur antihumedad</option>
                      <option value="Reforma completa">Reforma completa</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="area" className="block text-sm font-medium mb-2">
                        Metros cuadrados aproximados
                      </label>
                      <input
                        type="text"
                        id="area"
                        name="area"
                        value={formData.area}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-dark-bg border border-dark-lighter rounded-lg text-light-text text-sm focus:outline-none focus:border-primary-orange transition-colors"
                        placeholder="Ej: 20 m²"
                      />
                    </div>

                    <div>
                      <label htmlFor="neighborhood" className="block text-sm font-medium mb-2">
                        Barrio o localidad
                      </label>
                      <input
                        type="text"
                        id="neighborhood"
                        name="neighborhood"
                        value={formData.neighborhood}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-dark-bg border border-dark-lighter rounded-lg text-light-text text-sm focus:outline-none focus:border-primary-orange transition-colors"
                        placeholder="Ej: Centre, Sabadell"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Cuéntanos sobre tu proyecto <span className="text-primary-orange">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 bg-dark-bg border border-dark-lighter rounded-lg text-light-text text-sm focus:outline-none focus:border-primary-orange transition-colors resize-none"
                      placeholder="Describe brevemente qué necesitas (máximo 500 caracteres)"
                    ></textarea>
                    <p className="text-light-muted text-xs mt-2">
                      {formData.message.length}/500 caracteres
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-primary-orange text-white rounded-lg font-semibold text-lg hover:shadow-glow-orange transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar solicitud'}
                  </button>

                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-center"
                    >
                      <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                      ¡Mensaje enviado! Nos pondremos en contacto contigo pronto.
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-center"
                    >
                      <i className="ri-error-warning-line text-xl mr-2"></i>
                      Error al enviar. Por favor, llámanos o escríbenos por WhatsApp.
                    </motion.div>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-20 bg-gradient-to-br from-primary-orange to-primary-orange-light">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-white">
              ¿Prefieres llamar directamente?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Estamos disponibles para atenderte y resolver tus dudas sobre tu proyecto de pladur
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="flex items-center space-x-2 px-8 py-4 bg-white text-primary-orange rounded-lg font-semibold text-lg hover:shadow-xl transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>629 501 941</span>
              </a>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-8 py-4 border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-orange transition-all whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
            <p className="text-white/80 mt-6 text-sm">
              También puedes consultar nuestros <Link to="/servicios" className="text-white underline hover:text-white/90">servicios de pladur</Link> o resolver tus dudas en las <Link to="/faq" className="text-white underline hover:text-white/90">preguntas frecuentes</Link>
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
      <ScrollToTopButton />
    </div>
  );
};

export default ContactoPage;
